import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { 
  FileText, 
  Download, 
  RefreshCw, 
  User, 
  BookOpen, 
  CheckCircle, 
  AlertTriangle, 
  Calendar,
  Mail,
  CreditCard,
  GraduationCap,
  Edit,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import { loadStudentPhoto, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';

interface Student {
  ID: number;
  Name: string;
  Vorname: string;
  Klasse: string;
  RFID: string;
  sophomorixUnid: string;
  Diff_D: string;
  Diff_M: string;
  Diff_E: string;
  Memo?: string;
}

interface KIAssessment {
  unid: string;
  memo: string;
  datum: string;
  farbe: string;
}

interface PerformanceRecord {
  Fach: string;
  Baustein: string;
  Lernplan: string;
  Aufgabe: string;
  Lehrer: string;
  Datum: string;
}

interface StudentNote {
  id: number;
  unid: string;
  note: string;
  created_at: string;
  updated_at: string;
}

interface ExposeData {
  student: Student;
  kiAssessment: KIAssessment | null;
  performanceData: PerformanceRecord[];
  studentNotes: StudentNote[];
  generatedAt: string;
  totalPerformanceRecords: number;
}

interface StudentExposeProps {
  studentUnid: string;
  onClose: () => void;
}

export default function StudentExpose({ studentUnid, onClose }: StudentExposeProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [studentPhoto, setStudentPhoto] = useState<string | null>(null);

  // Fetch expose data
  const { data: exposeData, isLoading, error, refetch } = useQuery<ExposeData>(
    ['student-expose', studentUnid],
    () => api.get(`/students/${studentUnid}/expose`).then(res => res.data.data),
    {
      enabled: !!studentUnid && studentUnid !== 'undefined' && studentUnid !== 'null',
      retry: 1,
      onError: (error: any) => {
        console.error('Expose data error:', error);
        const errorMessage = error?.response?.data?.message || 'Fehler beim Laden der Exposé-Daten';
        toast.error(errorMessage);
      }
    }
  );

  // Foto laden wenn Daten verfügbar sind
  React.useEffect(() => {
    if (exposeData?.student?.sophomorixUnid) {
      loadStudentPhoto(exposeData.student.sophomorixUnid).then(photo => {
        setStudentPhoto(photo);
      });
    }
  }, [exposeData?.student?.sophomorixUnid]);

  const generatePDF = async () => {
    if (!exposeData) return;
    
    setIsGenerating(true);
    try {
      // Create a new window for PDF generation
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Popup-Blocker verhindert das Öffnen des PDF-Fensters');
        return;
      }

      // Generate HTML content for PDF
      const htmlContent = generateHTMLContent(exposeData);
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
        toast.success('PDF wurde erfolgreich generiert!');
      }, 500);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Fehler bei der PDF-Generierung');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateHTMLContent = (data: ExposeData) => {
    const formatDate = (dateString: string) => {
      return new Date(dateString).toLocaleDateString('de-DE');
    };

    const getKIAssessmentColor = (assessment: KIAssessment) => {
      // Check if memo contains specific keywords to determine color
      const memo = assessment.memo.toLowerCase();
      if (memo.includes('rot') || memo.includes('alarm') || memo.includes('dringend')) {
        return 'text-red-600';
      }
      if (memo.includes('grün') || memo.includes('gut') || memo.includes('positiv')) {
        return 'text-green-600';
      }
      return 'text-gray-600';
    };

    return `
      <!DOCTYPE html>
      <html lang="de">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Schüler-Exposé: ${data.student.Vorname} ${data.student.Name}</title>
        <style>
          @media print {
            body { margin: 0; padding: 20px; }
            .page-break { page-break-before: always; }
            .no-break { page-break-inside: avoid; }
          }
          
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background: white;
          }
          
          .header {
            text-align: center;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          
          .header h1 {
            color: #2563eb;
            font-size: 28px;
            margin: 0;
            font-weight: bold;
          }
          
          .header .subtitle {
            color: #6b7280;
            font-size: 16px;
            margin-top: 5px;
          }
          
          .student-info {
            display: flex;
            gap: 30px;
            margin-bottom: 30px;
            align-items: flex-start;
          }
          
          .student-photo {
            width: 120px;
            height: 120px;
            border: 3px solid #2563eb;
            border-radius: 10px;
            overflow: hidden;
            flex-shrink: 0;
          }
          
          .student-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .student-photo.placeholder {
            background: #f3f4f6;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #9ca3af;
            font-size: 14px;
          }
          
          .student-details {
            flex: 1;
          }
          
          .student-details h2 {
            color: #1f2937;
            font-size: 24px;
            margin: 0 0 15px 0;
            font-weight: bold;
          }
          
          .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 15px;
          }
          
          .info-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 0;
          }
          
          .info-item strong {
            color: #374151;
            min-width: 80px;
          }
          
          .info-item span {
            color: #6b7280;
          }
          
          .section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .section h3 {
            color: #2563eb;
            font-size: 20px;
            margin: 0 0 15px 0;
            padding-bottom: 8px;
            border-bottom: 2px solid #dbeafe;
            font-weight: bold;
          }
          
          .diff-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .diff-item {
            background: #f0f9ff;
            border: 2px solid #0ea5e9;
            border-radius: 8px;
            padding: 15px;
            text-align: center;
          }
          
          .diff-item h4 {
            margin: 0 0 8px 0;
            color: #0c4a6e;
            font-size: 14px;
            font-weight: bold;
          }
          
          .diff-value {
            font-size: 24px;
            font-weight: bold;
            color: #0ea5e9;
          }
          
          .assessment-box {
            background: #fef3c7;
            border: 2px solid #f59e0b;
            border-radius: 8px;
            padding: 15px;
            margin-bottom: 20px;
          }
          
          .assessment-box h4 {
            margin: 0 0 10px 0;
            color: #92400e;
            font-size: 16px;
            font-weight: bold;
          }
          
          .assessment-content {
            color: #78350f;
            line-height: 1.5;
          }
          
          .assessment-date {
            font-size: 12px;
            color: #a16207;
            margin-top: 8px;
          }
          
          .notes-section {
            background: #fdf2f8;
            border: 2px solid #ec4899;
            border-radius: 8px;
            padding: 15px;
          }
          
          .notes-section h4 {
            margin: 0 0 10px 0;
            color: #831843;
            font-size: 16px;
            font-weight: bold;
          }
          
          .note-item {
            background: white;
            border-radius: 6px;
            padding: 10px;
            margin-bottom: 10px;
            border-left: 4px solid #ec4899;
          }
          
          .note-text {
            color: #374151;
            margin-bottom: 5px;
          }
          
          .note-date {
            font-size: 12px;
            color: #9ca3af;
          }
          
          .performance-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
          }
          
          .performance-table th,
          .performance-table td {
            border: 1px solid #d1d5db;
            padding: 8px 12px;
            text-align: left;
          }
          
          .performance-table th {
            background: #f3f4f6;
            font-weight: bold;
            color: #374151;
          }
          
          .performance-table tr:nth-child(even) {
            background: #f9fafb;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #d1d5db;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
          }
          
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
            margin-bottom: 20px;
          }
          
          .stat-item {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 15px;
            text-align: center;
          }
          
          .stat-number {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 5px;
          }
          
          .stat-label {
            font-size: 12px;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Schüler-Exposé</h1>
          <div class="subtitle">Lernrakete - Detaillierte Schülerübersicht</div>
        </div>
        
        <div class="student-info">
          <div class="student-photo ${studentPhoto ? '' : 'placeholder'}">
            ${studentPhoto 
              ? `<img src="${createPhotoDataUrl(studentPhoto)}" alt="Schülerfoto">`
              : 'Kein Foto verfügbar'
            }
          </div>
          
          <div class="student-details">
            <h2>${data.student.Vorname} ${data.student.Name}</h2>
            <div class="info-grid">
              <div class="info-item">
                <strong>Klasse:</strong>
                <span>${data.student.Klasse}</span>
              </div>
              <div class="info-item">
                <strong>RFID:</strong>
                <span>${data.student.RFID || 'Nicht zugewiesen'}</span>
              </div>
              <div class="info-item">
                <strong>ID:</strong>
                <span>${data.student.sophomorixUnid}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="section">
          <h3>Fächer-Einstufungen</h3>
          <div class="diff-grid">
            <div class="diff-item">
              <h4>Deutsch</h4>
              <div class="diff-value">${data.student.Diff_D || '-'}</div>
            </div>
            <div class="diff-item">
              <h4>Mathematik</h4>
              <div class="diff-value">${data.student.Diff_M || '-'}</div>
            </div>
            <div class="diff-item">
              <h4>Englisch</h4>
              <div class="diff-value">${data.student.Diff_E || '-'}</div>
            </div>
          </div>
        </div>
        
                 ${data.kiAssessment ? `
         <div class="section">
           <h3>KI-Einschätzung</h3>
           <div class="assessment-box">
             <h4>Lernrakete Bewertung</h4>
             <div class="assessment-content">${data.kiAssessment.memo || 'Keine Bewertung verfügbar'}</div>
             <div class="assessment-date">Erstellt am: ${formatDate(data.kiAssessment.datum)}</div>
           </div>
         </div>
         ` : ''}
        
                 ${data.student.Memo ? `
         <div class="section">
           <h3>Notizen aus dem Lernbüro</h3>
           <div class="notes-section">
             <div class="note-item">
               <div class="note-text">${data.student.Memo}</div>
             </div>
           </div>
         </div>
         ` : ''}
        
        <div class="section">
          <h3>Leistungsdaten</h3>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">${data.totalPerformanceRecords}</div>
              <div class="stat-label">Leistungsnachweise</div>
            </div>
                         <div class="stat-item">
               <div class="stat-number">${data.student.Memo ? '1' : '0'}</div>
               <div class="stat-label">Notizen</div>
             </div>
          </div>
          
          ${data.performanceData.length > 0 ? `
          <table class="performance-table">
            <thead>
              <tr>
                <th>Fach</th>
                <th>Baustein</th>
                <th>Lernplan</th>
                <th>Aufgabe</th>
                <th>Lehrer</th>
                <th>Datum</th>
              </tr>
            </thead>
            <tbody>
              ${data.performanceData.map(record => `
                <tr>
                  <td>${record.Fach}</td>
                  <td>${record.Baustein}</td>
                  <td>${record.Lernplan}</td>
                  <td>${record.Aufgabe}</td>
                  <td>${record.Lehrer}</td>
                  <td>${formatDate(record.Datum)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          ` : '<p style="text-align: center; color: #6b7280; font-style: italic;">Keine Leistungsdaten verfügbar</p>'}
        </div>
        
        <div class="footer">
          <p>Exposé generiert am: ${formatDate(data.generatedAt)}</p>
          <p>Lernrakete - Schülerverwaltungssystem</p>
        </div>
      </body>
      </html>
    `;
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4">
          <div className="flex items-center justify-center space-x-3">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="text-lg font-medium text-gray-900">Lade Exposé-Daten...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Fehler beim Laden</h3>
            <p className="text-sm text-gray-500 mb-4">Die Exposé-Daten konnten nicht geladen werden.</p>
            <div className="flex space-x-3">
              <button
                onClick={() => refetch()}
                className="btn btn-primary bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Erneut versuchen
              </button>
              <button
                onClick={onClose}
                className="btn btn-secondary bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!exposeData) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Schüler-Exposé: {exposeData.student.Vorname} {exposeData.student.Name}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Schülerinformationen
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Name:</span>
                <span className="text-gray-900">{exposeData.student.Vorname} {exposeData.student.Name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Klasse:</span>
                <span className="text-gray-900">{exposeData.student.Klasse}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-700">RFID:</span>
                <span className="text-gray-900">{exposeData.student.RFID || 'Nicht zugewiesen'}</span>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-900 mb-3 flex items-center">
              <GraduationCap className="h-5 w-5 mr-2" />
              Fächer-Einstufungen
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{exposeData.student.Diff_D || '-'}</div>
                <div className="text-xs text-gray-600">Deutsch</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{exposeData.student.Diff_M || '-'}</div>
                <div className="text-xs text-gray-600">Mathe</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{exposeData.student.Diff_E || '-'}</div>
                <div className="text-xs text-gray-600">Englisch</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{exposeData.totalPerformanceRecords}</div>
              <div className="text-sm text-gray-600">Leistungsnachweise</div>
            </div>
          </div>
                     <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
             <div className="text-center">
               <div className="text-2xl font-bold text-orange-600">{exposeData.student.Memo ? '1' : '0'}</div>
               <div className="text-sm text-gray-600">Notizen</div>
             </div>
           </div>
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <div className="text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {exposeData.kiAssessment ? 'Ja' : 'Nein'}
              </div>
              <div className="text-sm text-gray-600">KI-Einschätzung</div>
            </div>
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={generatePDF}
            disabled={isGenerating}
            className="btn btn-primary bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            {isGenerating ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                <span>Generiere PDF...</span>
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                <span>PDF Exposé herunterladen</span>
              </>
            )}
          </button>
          
          <button
            onClick={onClose}
            className="btn btn-secondary bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
